<template>
  <BOffcanvas
    v-if="shouldRender"
    backdrop="true"
    backdrop-variant="dark"
    backdrop-class="shadow-lg"
    header="false"
    v-model="isVisibleUi"
    placement="start"
    @update:model-value="handleBackdropClick"
    style="background-color: #eaeaea"
    :class="{ 'slide-in': isVisibleUi, 'slide-out': !isVisibleUi }"
  >
    <div style="position: absolute; width: 0; height: 0"></div>

    <BNav class="cd-sidenav-menu">
      <div class="coats-logo">
        <div class="p-4">
          <img src="@/core/assets/logo.png" alt="Seamworks Logo" />
        </div>
      </div>
      <!-- Home Section -->
      <div class="cd-sidenav-menu-heading">{{ $t("headerSideMenuV1.menu") }}</div>
      <router-link :to="{ name: 'Dashboard' }" class="cd-sidenav-nav-link nav-link" @click="handleLinkClick">
        <font-awesome-icon :fixedWidth="true" :icon="['fas', 'tachometer-alt']" />{{ $t("headerSideMenuV1.dashboard") }}
      </router-link>

      <!-- Application specific items -->
      <AppSidebar @click="handleLinkClick" />
      <a
        v-if="Number(getAccountType()) == 101 && tenantSettingsLoaded && !isV4CalculationEngine"
        class="cd-sidenav-nav-link nav-link"
        @click="handleUploadClick"
        style="padding-right: 5px; cursor: pointer"
      >
        <font-awesome-icon :fixedWidth="true" :icon="['fas', 'upload']" />{{
          $t("headerSideMenuV1.uploadSeamworksDatabase")
        }}
      </a>

      <!-- System Admin items -->
      <template v-if="false && hasAnySystemPermissions">
        <div class="cd-sidenav-menu-heading">{{ $t("headerSideMenuV1.system") }}</div>
        <router-link
          :to="{ name: 'SystemUsersGrid' }"
          class="cd-sidenav-nav-link nav-link"
          v-permission="'AbpIdentity.Users'"
          @click="handleLinkClick"
        >
          <font-awesome-icon :fixedWidth="true" icon="users" />{{ $t("headerSideMenuV1.systemUsers") }}
        </router-link>
        <router-link
          :to="{ name: 'SystemRolesGrid' }"
          class="cd-sidenav-nav-link nav-link"
          v-permission="'AbpIdentity.Roles'"
          @click="handleLinkClick"
        >
          <font-awesome-icon :fixedWidth="true" icon="shield-alt" />{{ $t("headerSideMenuV1.systemRoles") }}
        </router-link>
      </template>

      <!-- SaaS Management items -->
      <template v-if="hasAnySaasPermissions">
        <div class="cd-sidenav-menu-heading">{{ $t("headerSideMenuV1.saas") }}</div>
        <router-link
          :to="{ name: 'HostFeaturesForm' }"
          class="cd-sidenav-nav-link nav-link"
          v-permission="'FeatureManagement.ManageHostFeatures'"
          @click="handleLinkClick"
        >
          <font-awesome-icon :fixedWidth="true" icon="desktop" />{{ $t("headerSideMenuV1.hostFeatures") }}
        </router-link>
        <router-link
          :to="{ name: 'TenantsGrid' }"
          class="cd-sidenav-nav-link nav-link"
          v-permission="'AbpTenantManagement.Tenants'"
          @click="handleLinkClick"
        >
          <font-awesome-icon :fixedWidth="true" icon="cogs" />{{ $t("headerSideMenuV1.tenants") }}
        </router-link>
      </template>

      <template v-if="hasAnyDebugFeature">
        <div class="cd-sidenav-menu-heading">{{ $t("headerSideMenuV1.debug") }}</div>
        <router-link
          :to="{ name: 'AuthInfo' }"
          class="cd-sidenav-nav-link nav-link"
          v-feature.bool="'CoatsDigitalCore.DisplayAuthDebug'"
          @click="handleLinkClick"
        >
          {{ hasAnyDebugFeature }}
          <font-awesome-icon :fixedWidth="true" icon="key" />{{ $t("headerSideMenuV1.authInfo") }}
        </router-link>
      </template>
    </BNav>
  </BOffcanvas>

  <DBUploadPage v-if="showUploadModal" :showModal="showUploadModal" @closeModal="showUploadModal = false" />
</template>

<script lang="ts">
import { PermissionGuard } from "@/core/plugins/permissions";
import { FeatureGuard } from "@/core/plugins/features";
import AppSidebar from "@/app/views/layout/AppSidebar.vue";
import { defineComponent } from "vue";
import { BNav, BOffcanvas } from "bootstrap-vue-next";
import { useStore } from "vuex";
import DBUploadPage from "@/app/views/_components/DBUploadPage.vue";
import { UserAdminSettingApi } from "@/api";

export default defineComponent({
  name: "HeaderSideMenuV1",
  components: {
    AppSidebar,
    BNav,
    BOffcanvas,
    DBUploadPage
  },
  emits: ["update:visible", "showUploadModal"],
  props: {
    id: {
      type: String,
      required: true
    },
    visible: {
      type: Boolean
    }
  },
  data() {
    const userAdminSettingApi = new UserAdminSettingApi()!;
    return {
      isVisibleUi: this.visible,
      shouldRender: false,
      slideClass: "slide-in",
      store: useStore(),
      showUploadModal: false,
      userAdminSettingApi,
      isV4CalculationEngine: false,
      tenantSettingsLoaded: false,
      latestLookupId: 0
    };
  },
  async created(): Promise<void> {
    await this.getCalculationEngineType();
  },
  watch: {
    // The calculation engine can only change on the System Configuration page,
    // so re-check in the background when the user navigates away from it.
    $route(to: any, from: any) {
      if (from?.name === "systemConfiguration") {
        this.getCalculationEngineType();
      }
    },
    visible(val) {
      if (val) {
        // The engine may have just been changed on this page without navigating yet
        if ((this as any).$route?.name === "systemConfiguration") {
          this.getCalculationEngineType();
        }
        this.shouldRender = true;
        this.slideClass = "slide-in";
        this.$nextTick(() => {
          this.isVisibleUi = true;
        });
      } else {
        this.slideClass = "slide-out";
        this.isVisibleUi = false;

        setTimeout(() => {
          this.shouldRender = false;
        }, 300);
      }
    },
    isVisibleUi(val) {
      this.$emit("update:visible", val);
    }
  },
  methods: {
    // GSD V4 tenants don't use the Seamworks 3.5.2 database upload flow, so that link is hidden for them.
    async getCalculationEngineType(): Promise<void> {
      // Give this check its own id. If a newer check starts while this one is, waiting for the server, this one stops and lets the newer one win.
      this.latestLookupId++;
      const myLookupId = this.latestLookupId;
      // Hide the link while re-checking, so a refresh never shows the stale engine value.
      this.tenantSettingsLoaded = false;
      try {
        const res = await this.userAdminSettingApi.apiSystemConfigurationGet();
        if (myLookupId !== this.latestLookupId) {
          return;
        }
        let engineType = "";
        if (res.data.calculationEngineType && res.data.calculationEngineType.defaultItem) {
          engineType = String(res.data.calculationEngineType.defaultItem).trim().toLowerCase();
        }
        if (engineType === "gsdv4") {
          this.isV4CalculationEngine = true;
        } else {
          this.isV4CalculationEngine = false;
        }
      } catch (e) {
        console.error("Failed to load calculation engine type", e);
      } finally {
        if (myLookupId === this.latestLookupId) {
          this.tenantSettingsLoaded = true;
        }
      }
    },
    handleLinkClick() {
      this.$emit("update:visible", false);
    },
    handleBackdropClick(newVal: any) {
      this.$emit("update:visible", newVal);
    },
    handleUploadClick() {
      this.showUploadModal = true;
      this.$emit("update:visible", false);
    },
    getAccountType() {
      let activeUser = (this as any).$store.getters.oidcUser;
      if (activeUser) {
        return (activeUser as any)["cd_account_type"] ?? "";
      }
      return "";
    }
  },
  computed: {
    systemUsersPermission(): boolean {
      return PermissionGuard("AbpIdentity.Users");
    },

    systemRolesPermission(): boolean {
      return PermissionGuard("AbpIdentity.Roles");
    },

    hasAnySystemPermissions(): boolean {
      return this.systemUsersPermission || this.systemRolesPermission;
    },

    hasAnyDebugFeature(): boolean {
      return FeatureGuard("CoatsDigitalCore.DisplayAuthDebug") === "true";
    },

    hasAnySaasPermissions(): boolean {
      return PermissionGuard("FeatureManagement.ManageHostFeatures") || PermissionGuard("AbpTenantManagement.Tenants");
    }
  }
});
</script>
<style>
.cd-sidenav-nav-link > svg {
  color: #58595b !important;
}
.cd-sidenav-menu-heading {
  font-size: 0.75rem !important;
  font-weight: bold !important;
  text-transform: uppercase !important;
  color: #b0b0af !important;
  letter-spacing: 1px !important;
}
.cd-sidenav-nav-link {
  color: #58595b !important;
}
.cd-sidenav-menu {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0 !important;
  font-size: 16px;
}
.offcanvas-body {
  flex-grow: 1;
  overflow-y: auto;
  padding: 0 !important;
  font-family: Arial, Helvetica, sans-serif;
}
.offcanvas {
  width: 320px !important;
  max-height: 100vh;
  overflow-y: auto;
  padding: 0px !important;
}
.offcanvas-start.slide-in {
  animation: slideInLeft 0.4s forwards;
}

.offcanvas-start.slide-out {
  animation: slideOutLeft 0.4s forwards;
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes slideOutLeft {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
}
</style>
